import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Settings, pimAnfCodes } from 'src/app/auth/models/settings';
import { SettingsRoleEnum } from 'src/app/auth/models/settings-role.enum';
import { AuthService } from 'src/app/auth/services/auth.service';
import { Header } from '../../models/header';
import { HeaderCluster } from '../../models/header-cluster';
import { HeaderItem } from '../../models/header-item';
import { LabHeader } from '../../models/lab-header';
import { UserHeader } from '../../models/user-header';
import { DevHeader } from '../../models/dev-header';
import { environment } from 'src/environments/environment';

/**
 * The links a pharmacy account keeps in the bottom bar, in this order; the rest of the menu is
 * under "Mais". A link the account does not have (PIM without the grant) is left out. A lab
 * account's menu is short enough to show whole.
 */
const BAR_URLS = ['', 'orders', 'foco', 'pim', 'campanhas', 'reporting'];

/** The bottom bar's icon for each link it can show (Material Icons). */
const BAR_ICONS: Record<string, string> = {
  '': 'home',
  'lab/home': 'home',
  orders: 'shopping_cart',
  foco: 'center_focus_strong',
  pim: 'medication',
  campanhas: 'campaign',
  reporting: 'bar_chart',
  'lab/reports': 'bar_chart',
  'lab/farmacias': 'local_pharmacy',
  'lab/marketplan': 'event_note',
};

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnInit {

  isDevMode = !environment.production;

  @Input()
  set settings(settings: Settings | null) {
    if (!!settings) {
      this.userRole = settings.role;
      this.priceManager = !!settings.priceManager;
      const operationalAccess = !!settings.operationalAccess;
      // PIM is shown when some pharmacy offers it; the role `dev` counts only at its own pharmacy (G7).
      const pimAccess = pimAnfCodes(settings).length > 0;
      if (settings.role === SettingsRoleEnum.lab) {
        this.header = new LabHeader(settings.name);
      } else if (settings.role === SettingsRoleEnum.dev) {
        this.header = new DevHeader(settings.name, this.priceManager, operationalAccess, pimAccess);
      } else {
        this.header = new UserHeader(
          settings.name,
          this.priceManager,
          pimAccess,
          operationalAccess
        );
      }
    } else {
      this.header = null;
      this.userRole = null;
      this.priceManager = false;
    }
    this.splitForBar();
  }

  @Output() loggedOut = new EventEmitter<void>();

  public header: Header | null = null;
  public userRole: SettingsRoleEnum | null = null;
  public priceManager = false;
  public SettingsRoleEnum = SettingsRoleEnum;
  public isExpanded: boolean = false;

  /** The bottom bar's links, and the menu clusters left for "Mais" (empty for a lab account). */
  public barItems: HeaderItem[] = [];
  public moreClusters: HeaderCluster[] = [];
  public moreOpen = false;
  public accountOpen = false;

  constructor(private authService: AuthService, private router: Router) {}

  ngOnInit(): void {}

  /** "Mais" as one list, in the menu's order. */
  get moreItems(): HeaderItem[] {
    return this.moreClusters.flatMap((c) => c.items);
  }

  /** "Mais" is marked while its menu is open, or when the page shown is one of its links. */
  get moreActive(): boolean {
    return (
      this.moreOpen ||
      this.moreClusters.some((c) =>
        c.items.some((i) =>
          this.router.isActive(this.router.createUrlTree([i.url]), {
            paths: 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
          })
        )
      )
    );
  }

  /** Where "Aplicações" goes, or null when the account has nowhere to switch to (AuthService). */
  get switcherUrl(): string | null {
    return this.userRole === SettingsRoleEnum.lab ? null : this.authService.switcherUrl;
  }

  /** The bottom bar's account button: the first letters of the first two words of the name. */
  get initials(): string {
    return (this.header?.name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  get homeUrl(): string {
    return this.userRole === SettingsRoleEnum.lab ? '/lab/home' : '/';
  }

  barIcon(item: HeaderItem): string {
    return BAR_ICONS[item.url] ?? 'circle';
  }

  barLabel(item: HeaderItem): string {
    return item.name === 'Home' ? 'Início' : item.name;
  }

  /** Only home is matched exactly; every other link is active on the pages under it. */
  isExactMatch(item: HeaderItem): boolean {
    return item.url === '' || item.url === 'lab/home';
  }

  onExpansionChange(expanded: boolean): void {
    this.isExpanded = expanded;
  }

  toggleMore(): void {
    this.moreOpen = !this.moreOpen;
    this.accountOpen = false;
  }

  toggleAccount(): void {
    this.accountOpen = !this.accountOpen;
    this.moreOpen = false;
  }

  closeMenus(): void {
    this.moreOpen = false;
    this.accountOpen = false;
  }

  logout(): void {
    this.closeMenus();
    this.loggedOut.emit();
  }

  private splitForBar(): void {
    const clusters = this.header?.clusters ?? [];
    if (this.userRole === SettingsRoleEnum.lab) {
      this.barItems = clusters.flatMap((c) => c.items);
      this.moreClusters = [];
      return;
    }
    const all = clusters.flatMap((c) => c.items);
    this.barItems = BAR_URLS.map((url) => all.find((i) => i.url === url)).filter(
      (i): i is HeaderItem => !!i
    );
    this.moreClusters = clusters
      .map((c) => ({ ...c, items: c.items.filter((i) => !BAR_URLS.includes(i.url)) }))
      .filter((c) => c.items.length > 0);
  }
}
